import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  HelpCircle,
  Loader2,
  X,
} from "lucide-react";
import {
  PAYROLL_SETUP_STEPS,
  buildPayrollSetupProgress,
  fetchPayrollSetupSnapshot,
} from "./payrollSetupPipeline";
import "../../../../../styles/salary-components-premium.css";

function Donut({ value, allComplete }) {
  const r = 26;
  const c = 2 * Math.PI * r;
  const off = c - (Math.min(100, Math.max(0, value)) / 100) * c;
  const tone = allComplete
    ? "var(--green-600)"
    : value >= 75
    ? "var(--amber-500)"
    : value >= 40
    ? "var(--amber-500)"
    : "var(--red-600)";
  return (
    <svg className="scp-donut" viewBox="0 0 64 64" width="68" height="68" role="img" aria-label={`${value}% complete`}>
      <circle cx="32" cy="32" r={r} fill="none" stroke="var(--slate-200)" strokeWidth="7" />
      <circle
        cx="32"
        cy="32"
        r={r}
        fill="none"
        stroke={tone}
        strokeWidth="7"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={off}
        transform="rotate(-90 32 32)"
      />
      <text x="32" y="36" textAnchor="middle" className="scp-donut-text">
        {allComplete ? "✓" : `${value}%`}
      </text>
    </svg>
  );
}

function ExplainModal({ open, onClose, stages }) {
  if (!open) return null;
  return (
    <div className="psp-modal-backdrop" role="dialog" aria-modal="true" aria-label="Payroll setup guide">
      <div className="psp-modal">
        <div className="psp-modal-head">
          <div>
            <h2 className="psp-modal-title">How payroll setup works</h2>
            <p className="psp-modal-sub">Complete these steps in order. Each page has only what you need for that step.</p>
          </div>
          <button type="button" className="psp-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <ol className="psp-modal-steps">
          {stages.map((s, i) => (
            <li key={s.key} className={`psp-modal-step ${s.done ? "done" : ""}`}>
              <span className="psp-modal-num">{s.done ? <CheckCircle2 size={16} /> : i + 1}</span>
              <div>
                <div className="psp-modal-step-title">{s.explainTitle || s.label}</div>
                <div className="psp-modal-step-text">{s.explainBody}</div>
                <div className="psp-modal-step-status">
                  {s.done ? "Completed" : "Pending"} · {s.detail}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

/**
 * Cross-page payroll setup progress bar with accurate % and guided navigation.
 */
export default function PayrollSetupProgress({ refreshKey = 0, compact = false, dropdown = false }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [snapshot, setSnapshot] = useState(null);
  const [explainOpen, setExplainOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchPayrollSetupSnapshot();
      setSnapshot(data);
    } catch {
      setSnapshot(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const progress = useMemo(
    () => (snapshot ? buildPayrollSetupProgress(snapshot) : null),
    [snapshot]
  );

  function goTo(path) {
    if (!path) return;
    const [pathname, hash] = path.split("#");
    navigate(pathname);
    if (hash) {
      requestAnimationFrame(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }

  if (loading && !progress) {
    if (dropdown) {
      return (
        <button type="button" className="scp-drop-trigger scp-pill-btn scp-pill-btn--motion" disabled>
          <Loader2 size={14} className="psp-spin" /> Payroll setup…
        </button>
      );
    }
    return (
      <section className="scp-card scp-progress psp-loading">
        <Loader2 size={20} className="psp-spin" />
        <span>Checking payroll setup progress…</span>
      </section>
    );
  }

  if (!progress) return null;

  const { stages, readiness, currentStep, allComplete, message, completedCount, totalSteps } = progress;

  const panel = (
    <section className={`scp-card scp-progress psp ${compact ? "psp-compact" : ""}${dropdown ? " psp-dropdown-panel" : ""}`}>
        <button
          type="button"
          className="scp-progress-meter psp-meter-btn"
          onClick={() => goTo(currentStep?.navigateTo)}
          title={allComplete ? "Setup complete" : `Continue: ${currentStep?.label}`}
        >
          <Donut value={readiness} allComplete={allComplete} />
          <div className="psp-meter-text">
            <div className="scp-progress-label">
              {allComplete ? "Payroll setup complete" : "Payroll setup progress"}
            </div>
            <div className="scp-progress-sub">{message}</div>
            {!allComplete && currentStep ? (
              <span className="psp-continue-hint">
                Click to continue <ChevronRight size={14} />
              </span>
            ) : null}
          </div>
        </button>

        <div className="psp-actions">
          {!allComplete && currentStep ? (
            <button
              type="button"
              className="bw-btn bw-btn-primary psp-continue-btn"
              onClick={() => goTo(currentStep.navigateTo)}
            >
              {currentStep.actionLabel || "Continue setup"} <ArrowRight size={15} />
            </button>
          ) : null}
          <button type="button" className="bw-btn bw-btn-secondary" onClick={() => setExplainOpen(true)}>
            <HelpCircle size={15} /> Explain this page
          </button>
        </div>

        <div className="scp-stages psp-stages">
          {stages.map((s) => (
            <button
              type="button"
              key={s.key}
              className={`scp-stage psp-stage-btn ${s.done ? "done" : s.key === currentStep?.key ? "current" : "pending"}`}
              onClick={() => goTo(s.navigateTo)}
              title={`Go to ${s.label}`}
            >
              <span className="scp-stage-icon">
                {s.done ? <CheckCircle2 size={16} /> : s.key === currentStep?.key ? <Clock size={16} /> : <Clock size={16} />}
              </span>
              <div className="scp-stage-text">
                <span className="scp-stage-label">{s.label}</span>
                <span className="scp-stage-detail">{s.detail}</span>
              </div>
              {!s.done ? <ChevronRight size={14} className="psp-stage-arrow" /> : null}
            </button>
          ))}
        </div>

        <div className="psp-step-counter">
          {completedCount} of {totalSteps} steps complete
          {!allComplete ? ` · Next: ${currentStep?.shortLabel || currentStep?.label}` : ""}
        </div>
      </section>
  );

  if (dropdown) {
    const summary = allComplete
      ? "Payroll setup complete"
      : `Payroll setup ${readiness}% · ${currentStep?.shortLabel || currentStep?.label || "Continue"}`;
    return (
      <>
        <div className="scp-drop-wrap">
          <button
            type="button"
            className={`scp-drop-trigger scp-pill-btn scp-pill-btn--motion${panelOpen ? " is-open" : ""}`}
            aria-expanded={panelOpen}
            onClick={() => setPanelOpen((v) => !v)}
          >
            <span className="scp-drop-trigger-label">{summary}</span>
            <ChevronDown size={14} className="scp-drop-chevron" aria-hidden />
          </button>
          {panelOpen ? panel : null}
        </div>
        <ExplainModal
          open={explainOpen}
          onClose={() => setExplainOpen(false)}
          stages={stages.length ? stages : PAYROLL_SETUP_STEPS.map((s) => ({ ...s, done: false, detail: "—" }))}
        />
      </>
    );
  }

  return (
    <>
      {panel}
      <ExplainModal
        open={explainOpen}
        onClose={() => setExplainOpen(false)}
        stages={stages.length ? stages : PAYROLL_SETUP_STEPS.map((s) => ({ ...s, done: false, detail: "—" }))}
      />
    </>
  );
}
